import { Injectable } from '@angular/core';
import { Group, PagedResult, SaveSegmentInput, Segment } from '@core/interfaces';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SegmentService {
  constructor(private readonly apollo: Apollo) {}

  async loadData({ where, options }): Promise<PagedResult<Segment>> {
    return this.apollo
      .query({
        query: gql`
          query listSegments($where: SegmentWhereInput, $orderBy: SegmentOrderByInput, $limit: Int, $offset: Int) {
            segments(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
              pageInfo{
                hasNextPage
                hasPreviousPage
              }
              totalCount
              items {
                id
                name
                archived
                createdAt
              }
            }
          }
        `,
        variables: {
          orderBy: options.orderBy,
          limit: options.limit,
          offset: options.offset,
          where: where
        }
      })
      .toPromise()
      .then((response) => response.data['segments'] as PagedResult<Segment>)
      .then((data) => {
        return {
          items: data.items,
          totalCount: data.totalCount,
          filtered: Boolean(!!where.name_contains || where.archived !== false)
        };
      });
  }

  public async getSegmentById(id: string): Promise<Segment | null> {
    return this.apollo
      .query({
        query: gql`
          query getSegmentById($id: ID!) {
            segment(id: $id) {
              id
              name
              archived
              createdAt
              maintenanceTypes {
                id
                name
                archived
              }
              segmentMaintenanceTypeForms {
                form {
                  id
                  name
                }
                maintenanceType {
                  id
                  name
                }
                requiredComplete
                requiredStart
                requiredReport
              }
            }
          }
        `,
        variables: {
          id
        }
      })
      .toPromise()
      .then((response) => response.data['segment']);
  }

  private async updateSegment(id: string, input: any): Promise<Segment> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation updateSegment($id: ID!, $input: SegmentInput!) {
            updateSegment(id: $id, input: $input) {
              id
              name
              archived
              createdAt
              maintenanceTypes {
                id
                name
              }
              segmentMaintenanceTypeForms {
                form {
                  id
                }
                maintenanceType {
                  id
                }
                requiredComplete
                requiredStart
                requiredReport
              }
            }
          }
        `,
        variables: {
          id,
          input
        }
      })

      .toPromise()
      .then((response) => response.data['updateSegment'] as Segment);
  }

  private async createSegment(input: any): Promise<Segment> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createSegment($input: SegmentInput!) {
          createSegment(input: $input) {
            id
            name
            archived
            createdAt
            segmentMaintenanceTypeForms {
              form {
                id
              }
              maintenanceType {
                id
              }
              requiredComplete
              requiredStart
              requiredReport
            }
          }
        }
        `,
        variables: {
          input
        }
      })
      .toPromise()
      .then((response) => response.data['createSegment'] as Segment);
  }

  public async save(segmentId: string, segmentInput: SaveSegmentInput): Promise<Segment> {
    if (segmentId) {
      return this.updateSegment(segmentId, segmentInput);
    }
    return this.createSegment(segmentInput);
  }

  public async listGroups({ where, segmentId, limit, offset }: { where: any, segmentId?: string, limit: number, offset: number }): Promise<PagedResult<Group>> {
    return this.apollo
      .query({
        query: gql`
          query listGroups($where: GroupWhereInput, $segmentId: ID $limit: Int, $offset:Int) {
            groups(where: $where, orderBy: name_asc, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                type
                archived
                description
                locked
                segmentsConnection(where: { id: $segmentId }, limit: 1, offset: 0){
                  items {
                    id
                  }
                }
              }
            }
          }`,
        variables: {
          where,
          segmentId,
          limit,
          offset
        }
      })
      .toPromise()
      .then((response) => response.data['groups'] as PagedResult<Group>);
  }

  public async addSegmentToGroup({ segmentId, groupIds }: { segmentId: string, groupIds: string[], }): Promise<Segment[]> {
    return this.apollo.mutate({
      mutation: gql`
        mutation addSegmentToGroup($input: AddSegmentToGroupInput! $segmentId: ID!) {
          addSegmentToGroup(input: $input) {
            segment {
              id
            }
            group {
              id
              segmentsConnection(where: { id: $segmentId }, limit: 1, offset: 0){
                items {
                  id
                }
              }
            }
          }
        }
      `,
      variables: {
        segmentId: segmentId,
        input: {
          items: groupIds.map((groupId) => ({
            segmentId: segmentId,
            groupId: groupId
          }))
        }
      }
    }).toPromise()
      .then((response: any) => response.data.addSegmentToGroup.map((item: { segment: Segment }) => item && item.segment));
  }

  public async removeSegmentFromGroup(items: Array<{ segmentId: string, groupId: string }>): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation removeSegmentFromGroup($input: RemoveSegmentFromGroupInput!) {
          removeSegmentFromGroup(input: $input) {
            group {
              id
            }
            segment {
              id
            }
          }
        }
      `,
      variables: {
        input: {
          items
        }
      }
    }).toPromise();
  }

}
